import { defineStore } from 'pinia'
import { getDashboardOverview, getTodoSummary, refreshDashboard } from '@/api/dashboard'
import { getUnreadCount } from '@/api/notification'

/**
 * 工作台Store
 */
export const useDashboardStore = defineStore('dashboard', {
  state: () => ({
    // 总览数据
    overview: null,
    role: 'employee',

    // 待办数据
    todoSummary: null,
    todoCategory: 'all',
    todoPage: 1,
    todoSize: 8,

    // 通知未读数
    unreadCount: {
      total: 0,
      BUSINESS: 0,
      APPROVAL: 0,
      SYSTEM: 0
    },

    // 通知推送版本号：每次收到 SSE NOTIFICATION 时 +1
    // 用于让 NotificationBell 等组件 watch 到新通知到达，触发列表刷新
    notificationVersion: 0,

    // 加载状态
    overviewLoading: false,
    todoLoading: false,
    refreshing: false
  }),

  getters: {
    /** KPI数据 */
    kpiData: (state) => state.overview?.kpi || {},

    /** 图表数据 */
    chartData: (state) => state.overview?.chartData || {},

    /** 待办列表 */
    todoList: (state) => state.todoSummary?.list || [],

    /** 待办统计 */
    todoCounts: (state) => ({
      total: state.todoSummary?.total || 0,
      overdue: state.todoSummary?.overdue || 0,
      expiring: state.todoSummary?.expiring || 0,
      approval: state.todoSummary?.approval || 0,
      task: state.todoSummary?.task || 0
    }),

    /** 总未读通知数 */
    totalUnread: (state) => state.unreadCount.total || 0
  },

  actions: {
    /** 加载工作台总览 */
    async loadOverview() {
      this.overviewLoading = true
      try {
        const res = await getDashboardOverview()
        if (res.data) {
          this.overview = res.data
          this.role = res.data.role || 'employee'
        }
      } catch (e) {
        console.error('加载工作台总览失败:', e)
      } finally {
        this.overviewLoading = false
      }
    },

    /** 加载待办事项 */
    async loadTodos(category, page) {
      if (category !== undefined) this.todoCategory = category
      if (page !== undefined) this.todoPage = page

      this.todoLoading = true
      try {
        const res = await getTodoSummary({
          category: this.todoCategory,
          page: this.todoPage,
          size: this.todoSize
        })
        if (res.data) {
          this.todoSummary = res.data
        }
      } catch (e) {
        console.error('加载待办事项失败:', e)
      } finally {
        this.todoLoading = false
      }
    },

    /** 加载未读通知数量 */
    async loadUnreadCount() {
      try {
        const res = await getUnreadCount()
        if (res.data) {
          this.unreadCount = res.data
        }
      } catch (e) {
        console.error('加载未读通知数失败:', e)
      }
    },

    /** 手动刷新全部数据 */
    async refreshAll() {
      this.refreshing = true
      try {
        await refreshDashboard()
        await Promise.all([
          this.loadOverview(),
          this.loadTodos(),
          this.loadUnreadCount()
        ])
      } catch (e) {
        console.error('刷新工作台数据失败:', e)
      } finally {
        this.refreshing = false
      }
    },

    /** 初始加载（登录后调用一次） */
    async init() {
      await Promise.all([
        this.loadOverview(),
        this.loadTodos('all', 1),
        this.loadUnreadCount()
      ])
    },

    /** 处理 SSE 通知推送 */
    handleNotificationPush() {
      // 收到推送后更新未读数
      this.loadUnreadCount()
      // 通知所有订阅方（如 NotificationBell）有新通知到达
      this.notificationVersion++
    }
  }
})
